import type { DocumentPageSchema } from '@avisoma/shared';
import { Link } from '../../components/Link';
import { LazyImage } from '../../components/LazyImage';
import { FullScreenImagePreviewTrigger } from '../../components/FullScreenImagePreview';
import { PagePreviewHoverCard } from '../../components/pagePreview/PagePreviewHoverCard';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';

// One child segment-page preview inside the Segments card: image + section
// heading + top-1 classification. `child` is undefined while its query is in
// flight (the parent only knows the id at that point), so we keep the slot and
// show a skeleton instead of collapsing the grid.
export function SegmentChildThumb({
  childId,
  child,
  loading,
  error,
  siblingIds,
  onHover,
  onLeave,
}: {
  childId: string;
  child: DocumentPageSchema | undefined;
  loading: boolean;
  error: boolean;
  // Ordered ids of all sibling segments, so the full-screen preview can flip
  // between sections with prev/next + arrow keys.
  siblingIds?: string[];
  // Hovering a thumbnail highlights this section's band on the parent preview.
  onHover?: () => void;
  onLeave?: () => void;
}) {
  const order = child?.sectionBounds?.order;
  const heading = child?.sectionBounds?.heading?.trim();
  const topClassification = child?.processing?.classification?.result?.[0]?.[0];

  return (
    <PagePreviewHoverCard
      // No inline classification: the parent polls this child's query every 2.5s,
      // so let the popover read the (shared, live) fetched cache instead of a
      // snapshot frozen at mouseenter.
      page={{ id: childId }}
      className="min-w-0 space-y-1"
      onMouseEnter={() => onHover?.()}
      onMouseLeave={() => onLeave?.()}
    >
      <div className="aspect-3/4 group relative overflow-hidden rounded-md border border-gray-200 bg-gray-100 shadow-sm dark:border-zinc-700 dark:bg-zinc-700">
        <Link to={`/document-pages/${childId}`} className="block h-full w-full transition hover:opacity-90">
          <LazyImage src={pagePreviewSrc(childId, { resize: 'x400' })} className="w-full h-full" />
        </Link>
        <FullScreenImagePreviewTrigger pageId={childId} pageIds={siblingIds} />
      </div>

      <Link
        to={`/document-pages/${childId}`}
        className="block truncate text-center text-xs font-bold text-gray-700 dark:text-zinc-200"
        title={heading || undefined}
      >
        {typeof order === 'number' ? `${order + 1}. ` : ''}
        {heading && heading.length > 0 ? heading : 'Untitled section'}
      </Link>

      <div className="truncate text-center text-xs text-gray-500 dark:text-zinc-400" title={topClassification ?? undefined}>
        {error
          ? 'Unavailable'
          : loading && !child
            ? 'Loading…'
            : (topClassification ?? 'Unclassified')}
      </div>
    </PagePreviewHoverCard>
  );
}
